import {FadeInView} from '@/components/basic/animations';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FlatList, ListRenderItemInfo, RefreshControl, View} from 'react-native';
import theme from '@style';
import {useInnerStyle} from './casino.hooks';
import {
  NewGameListItem,
  NewGameListParams,
  postNewGameList,
} from '@/common-pages/casino/casino.service';
import globalStore from '@/services/global.state';
import DetailNavTitle from '@businessComponents/detail-nav-title';
import {NoMoreData} from '@basicComponents/default-page';
import {useFocusEffect, useRoute} from '@react-navigation/native';
import NoData from '@/components/basic/error-pages/no-data';
import {useTranslation} from 'react-i18next';
import CasinoTabs from './casino-tabs';
import {goBack, groupArrayByLength} from '@/utils';
import {Image} from 'antd-mobile';
import CasinoSelect from './casino-select';
import {getGameCategory} from './casino.service';
import {normalizeGamePush} from '@utils/game';
import {getGamePicUrl, GamePicType} from '@/components/utils';

const Casino = () => {
  const {params = {}} = useRoute<any>();
  const [gameTypes, setGameTypes] = useState<{label: string; value: string}[]>(
    [],
  );

  const {i18n} = useTranslation();
  const [gameType, setGameType] = useState<string>('');
  const {
    size: {screenWidth, screenHeight},
  } = useInnerStyle();
  const [gameList, setGameList] = useState<NewGameListItem[]>([]);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [provider, setProvider] = useState<string>('');
  const [sort, setSort] = useState<string>('');
  const pageNo = useRef(1);
  const totalPage = useRef(1);

  const pairedGameList = useMemo(() => {
    return groupArrayByLength(gameList, 3);
  }, [gameList]);

  const getGameCategories = () => {
    // 获取游戏分类
    getGameCategory('casino').then(res => {
      const gamesList = res.map(v => {
        return {
          label: v.name,
          value: v.id,
        };
      });
      setGameTypes([{label: 'All', value: ''}, ...gamesList]);
    });
  };

  const handleFocusEffect = useCallback(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      if (token) {
        globalStore.amountCheckOut.next({});
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);
  useFocusEffect(handleFocusEffect);

  const getList = useCallback(
    (showloading = true) => {
      showloading && globalStore.globalLoading.next(true);
      const requestParams: NewGameListParams = {
        l1CategoryId: 2,
        l2CategoryId: gameType,
        pageNo: pageNo.current,
        // provider,
        providerId: provider,
        pageSize: 21,
      };
      if (sort) {
        requestParams.orderByField = 'name';
        requestParams.isAsc = sort === 'A-Z';
      }
      return postNewGameList(requestParams)
        .then(game => {
          if (pageNo.current <= 1) {
            setGameList(game.content || []);
          } else {
            setGameList(_gameList => [..._gameList, ...(game.content || [])]);
          }

          totalPage.current = game.totalPages;
        })
        .finally(() => {
          globalStore.globalLoading.next(false);
          setRefreshing(false);
        });
    },
    [gameType, provider, sort],
  );

  const refreshNextPage = () => {
    if (totalPage.current === 1) {
      return;
    }
    pageNo.current++;
    if (pageNo.current > totalPage.current) {
      return;
    }
    globalStore.globalLoading.next(true);
    getList(true);
  };

  const computedSize = (num: number) => (num * screenWidth) / 375;
  const size = {
    computedS: computedSize(theme.paddingSize.s),
    computedL: computedSize(theme.paddingSize.l),
  };

  const renderItem = ({
    item: subGameList,
    index,
  }: ListRenderItemInfo<NewGameListItem[]>) => {
    return (
      <div className="grid grid-cols-3 gap-2 px-3" key={index}>
        {subGameList.map(item => (
          <div
            key={item.id}
            className="h-0 pb-[139%] relative rounded-lg overflow-hidden"
            onClick={() => normalizeGamePush(item.id, true)}>
            <div className="absolute size-full">
              <Image
                src={getGamePicUrl(
                  GamePicType.V_RECTANGLE,
                  item.source,
                  item.tripartiteUniqueness,
                )}
                className="size-full"
                fit="fill"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    getGameCategories();
  }, []);
  useEffect(() => {
    const {provider: initProvider, gameType: initGameType} =
      (params as any) || {};
    if (initProvider !== undefined) {
      setProvider(String(initProvider));
    }
    if (initGameType !== undefined) {
      setGameType(String(initGameType));
    }
  }, [params]);
  useEffect(() => {
    pageNo.current = 1;
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, provider, sort]);
  const handleChangeProvider = (value: string) => {
    console.log('handleChangeProvider', value);
    setProvider(value);
  };

  const handleChangeSort = (value: string) => {
    setSort(value);
  };
  return (
    <FadeInView style={[{height: screenHeight}, theme.background.lightGrey]}>
      <DetailNavTitle
        title={i18n.t('home.tab.casino')}
        hideServer
        onBack={goBack}
      />
      <CasinoTabs
        tabOptions={gameTypes}
        value={gameType}
        onChange={setGameType}
      />
      <CasinoSelect
        type="casino"
        handleChangeProvider={handleChangeProvider}
        handleChangeSort={handleChangeSort}
        initialProvider={params.provider}
      />
      <FlatList
        style={[theme.flex.flex1]}
        data={pairedGameList}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              getList(false);
            }}
          />
        }
        ListFooterComponentStyle={[theme.fill.fillW, theme.flex.center]}
        contentContainerStyle={{
          gap: size.computedS,
          paddingVertical: size.computedL,
        }}
        onEndReached={refreshNextPage}
        ListEmptyComponent={
          <View
            style={[theme.padding.xxl, theme.flex.center, theme.fill.fillW]}>
            <NoData />
          </View>
        }
        ListFooterComponent={
          gameList &&
          gameList.length > 0 &&
          pageNo.current >= totalPage.current ? (
            <NoMoreData />
          ) : undefined
        }
      />
    </FadeInView>
  );
};

export default Casino;
